import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import usePeriodChange from '@/hooks/usePeriodChange';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const Rekapan = ({ branchId }) => {
  const [loading, setLoading] = useState(true);
  const [rekapData, setRekapData] = useState([]);
  const [branchName, setBranchName] = useState('');
  const [defaultOperationalCost, setDefaultOperationalCost] = useState(100);

  useEffect(() => {
    fetchRekapData();
  }, [branchId]);

  usePeriodChange(() => fetchRekapData(), { includeSettings: true });

  const fetchRekapData = async () => {
    setLoading(true);
    try {
      // Get selected period from localStorage
      const periodId = localStorage.getItem('selectedPeriodId');
      
      // Build query params
      const params = periodId ? { period_id: periodId } : {};
      
      // Fetch branch name
      if (branchId) {
        try {
          const branchRes = await api.get(`/branches/${branchId}`);
          setBranchName(branchRes.data.name);
        } catch (error) {
          setBranchName('');
        }
      } else {
        setBranchName('Semua Cabang');
      }

      // Fetch purchases, sales, expenses, and system settings with period filter
      const [purchasesRes, salesRes, expensesRes, settingsRes, periodsRes] = await Promise.all([
        api.get('/purchases', { params }),
        api.get('/sales', { params }),
        api.get('/expenses', { params }),
        api.get('/settings'),
        api.get('/periods')
      ]);
      
      // Operational cost default now comes from the selected PERIOD (fallback to global setting)
      const currentPeriod = (periodId && periodsRes.data) ? periodsRes.data.find(p => p.id === periodId) : null;
      const defaultCost = (currentPeriod && currentPeriod.default_operational_cost)
        ? currentPeriod.default_operational_cost
        : (settingsRes.data?.default_operational_cost || 0);
      const overrides = settingsRes.data?.branch_operational_overrides || [];
      const branchOverride = branchId
        ? overrides.find((o) => o.branch_id === branchId)
        : null;
      const currentOperationalCost = branchOverride ? branchOverride.cost_per_gr : defaultCost;
      setDefaultOperationalCost(currentOperationalCost);

      const purchases = branchId 
        ? purchasesRes.data.filter(p => p.branch_id === branchId)
        : purchasesRes.data;
      
      const sales = branchId
        ? salesRes.data.filter(s => s.branch_id === branchId)
        : salesRes.data;
      
      const expenses = branchId
        ? expensesRes.data.filter(e => e.branch_id === branchId)
        : expensesRes.data;

      // Get unique partais from purchases
      const partais = [...new Set(purchases.map(p => p.partai))].filter(Boolean);

      // Show a "Tanpa Partai" group when any purchase/sale/expense has no partai
      // (e.g. a biaya without partai even when there's no purchase without partai)
      const NO_PARTAI = '(Tanpa Partai)';
      const hasNoPartai = purchases.some(p => !p.partai) || sales.some(s => !s.partai) || expenses.some(e => !e.partai);
      const partaisWithNoGroup = hasNoPartai ? [...partais, NO_PARTAI] : partais;

      // Calculate data for each partai
      const calculatedData = partaisWithNoGroup.map(partai => {
        const isNoPartai = partai === NO_PARTAI;
        // Filter data for this partai
        const partaiPurchases = purchases.filter(p => isNoPartai ? !p.partai : p.partai === partai);
        const partaiSales = sales.filter(s => isNoPartai ? !s.partai : s.partai === partai);
        const partaiExpenses = expenses.filter(e => isNoPartai ? !e.partai : e.partai === partai);

        // Calculate stok awal from purchases with keterangan "Stok Awal"
        const stokAwalGr = partaiPurchases.reduce((sum, p) => {
          if (p.items) {
            const stokAwalWeight = p.items
              .filter(item => item.keterangan === 'Stok Awal')
              .reduce((itemSum, item) => itemSum + (item.weight_kg || 0), 0);
            return sum + stokAwalWeight;
          }
          return sum;
        }, 0);

        // Calculate total weights (already in grams from user input)
        const totalBeratBeli = partaiPurchases.reduce((sum, p) => {
          const purchaseWeight = p.items ? p.items.reduce((itemSum, item) => itemSum + (item.weight_kg || 0), 0) : 0;
          return sum + purchaseWeight;
        }, 0);

        // Berat Beli = Total Berat Beli - Stok Awal
        const beratBeli = totalBeratBeli - stokAwalGr;
        
        // Calculate berat jual (exclude items with keterangan "Stok")
        const beratJual = partaiSales.reduce((sum, s) => {
          if (s.keterangan !== 'Stok') {
            return sum + (s.quantity_kg || 0);
          }
          return sum;
        }, 0);
        
        // Calculate stok akhir from sales with keterangan "Stok"
        const stokAkhirGr = partaiSales.reduce((sum, s) => {
          if (s.keterangan === 'Stok') {
            return sum + (s.quantity_kg || 0);
          }
          return sum;
        }, 0);

        // Calculate +/- gr
        const plusMinusGr = (beratJual + stokAkhirGr) - (beratBeli + stokAwalGr);
        const plusMinusPersen = (beratBeli + stokAwalGr) > 0 ? (plusMinusGr / (beratBeli + stokAwalGr)) * 100 : 0;

        // Calculate Rp Stok Awal from purchases with keterangan "Stok Awal"
        const rpStokAwal = partaiPurchases.reduce((sum, p) => {
          if (p.items) {
            const stokAwalAmount = p.items
              .filter(item => item.keterangan === 'Stok Awal')
              .reduce((itemSum, item) => itemSum + (item.subtotal || 0), 0);
            return sum + stokAwalAmount;
          }
          return sum;
        }, 0);

        // Calculate total rupiah values
        const totalRpBeli = partaiPurchases.reduce((sum, p) => sum + (p.total_amount || 0), 0);
        
        // Rp Beli = Total Rp Beli - Rp Stok Awal
        const rpBeli = totalRpBeli - rpStokAwal;
        
        // Calculate rpJual (exclude sales with keterangan "Stok")
        const rpJual = partaiSales.reduce((sum, s) => {
          if (s.keterangan !== 'Stok') {
            return sum + (s.total_amount || 0);
          }
          return sum;
        }, 0);
        
        // Calculate rpStokAkhir from sales with keterangan "Stok"
        const rpStokAkhir = partaiSales.reduce((sum, s) => {
          if (s.keterangan === 'Stok') {
            return sum + (s.total_amount || 0);
          }
          return sum;
        }, 0);

        // Calculate costs - separate biayaOP from biayaPusat
        // Biaya OP: Manual expenses only (exclude auto-generated operational cost expenses)
        const biayaOP = partaiExpenses.reduce((sum, e) => {
          // Exclude auto-generated expenses
          if (e.description && e.description.includes('Biaya Operasional - Partai')) {
            return sum;
          }
          return sum + (e.amount || 0);
        }, 0);
        const biayaOPPerGr = beratJual > 0 ? biayaOP / beratJual : 0;
        // Biaya Pusat: Auto-calculated operational cost
        const biayaPusat = beratJual * currentOperationalCost;

        // Calculate profit/loss
        const labaRugi = rpJual + rpStokAkhir - rpStokAwal - rpBeli - biayaOP - biayaPusat;
        // LabaRugi/gr = LabaRugi / Berat Jual
        const labaRugiPerGr = beratJual > 0 ? labaRugi / beratJual : 0;

        return {
          partai,
          stokAwalGr,
          beratBeli,
          beratJual,
          stokAkhirGr,
          stok: stokAkhirGr, // Keep for backwards compatibility
          plusMinusGr,
          plusMinusPersen,
          rpStokAwal,
          rpBeli,
          rpJual,
          rpStokAkhir,
          rpStok: rpStokAkhir, // Keep for backwards compatibility
          biayaOP,
          biayaOPPerGr,
          biayaPusat,
          labaRugi,
          labaRugiPerGr
        };
      });

      setRekapData(calculatedData);
    } catch (error) {
      console.error('Error fetching rekap data:', error);
      toast.error('Gagal memuat data rekapan');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return Math.round(num).toLocaleString('id-ID');
  };

  const formatCurrency = (num) => {
    return `Rp.\u00a0${Math.round(num).toLocaleString('id-ID')}`;
  };

  const getPercentageColor = (percent) => {
    if (percent < 0) return 'bg-red-100 text-red-800';
    if (percent >= 5) return 'bg-green-100 text-green-800';
    return '';
  };

  const getLabaRugiColor = (value) => {
    if (value < 0) return 'bg-red-100 text-red-800';
    if (value > 499) return 'bg-green-100 text-green-800';
    return '';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-slate-600">Memuat data rekapan...</p>
        </CardContent>
      </Card>
    );
  }

  if (rekapData.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-slate-600">Belum ada data rekapan.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rekapan per Partai : {branchName}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs whitespace-nowrap">Partai</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Stok Awal (gr)</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Berat Beli (gr)</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Berat Jual (gr)</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Stok Akhir (gr)</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">+/- gr</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">+/- gr (%)</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Rp Stok Awal</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Rp Beli</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Rp Jual</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Rp Stok Akhir</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Biaya OP</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Biaya OP/gr</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Biaya Pusat (Rp.&nbsp;{formatNumber(defaultOperationalCost)}/gr)</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">LabaRugi</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">LabaRugi/gr</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rekapData.map((row, index) => (
                <TableRow key={row.partai} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                  <TableCell className="font-medium whitespace-nowrap">{row.partai}</TableCell>
                  <TableCell className="text-right">{formatNumber(row.stokAwalGr || 0)}</TableCell>
                  <TableCell className="text-right">{formatNumber(row.beratBeli)}</TableCell>
                  <TableCell className="text-right">{formatNumber(row.beratJual)}</TableCell>
                  <TableCell className="text-right">{formatNumber(row.stokAkhirGr || row.stok || 0)}</TableCell>
                  <TableCell className="text-right">{formatNumber(row.plusMinusGr)}</TableCell>
                  <TableCell className={`text-right ${getPercentageColor(row.plusMinusPersen)}`}>
                    {row.plusMinusPersen.toFixed(2)}%
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(row.rpStokAwal || 0)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(row.rpBeli)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(row.rpJual)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(row.rpStokAkhir || row.rpStok || 0)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(row.biayaOP)}</TableCell>
                  <TableCell className="text-right">{formatNumber(row.biayaOPPerGr)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(row.biayaPusat)}</TableCell>
                  <TableCell className="text-right font-bold">{formatCurrency(row.labaRugi)}</TableCell>
                  <TableCell className={`text-right font-bold ${getLabaRugiColor(row.labaRugiPerGr)}`}>
                    {formatNumber(row.labaRugiPerGr)}
                  </TableCell>
                </TableRow>
              ))}
              {/* Total Row */}
              {rekapData.length > 0 && (() => {
                const totals = rekapData.reduce((acc, row) => ({
                  stokAwalGr: acc.stokAwalGr + (row.stokAwalGr || 0),
                  beratBeli: acc.beratBeli + row.beratBeli,
                  beratJual: acc.beratJual + row.beratJual,
                  stokAkhirGr: acc.stokAkhirGr + (row.stokAkhirGr || row.stok || 0),
                  plusMinusGr: acc.plusMinusGr + row.plusMinusGr,
                  rpStokAwal: acc.rpStokAwal + (row.rpStokAwal || 0),
                  rpBeli: acc.rpBeli + row.rpBeli,
                  rpJual: acc.rpJual + row.rpJual,
                  rpStokAkhir: acc.rpStokAkhir + (row.rpStokAkhir || row.rpStok || 0),
                  biayaOP: acc.biayaOP + row.biayaOP,
                  biayaPusat: acc.biayaPusat + row.biayaPusat,
                  labaRugi: acc.labaRugi + row.labaRugi
                }), {
                  stokAwalGr: 0,
                  beratBeli: 0,
                  beratJual: 0,
                  stokAkhirGr: 0,
                  plusMinusGr: 0,
                  rpStokAwal: 0,
                  rpBeli: 0,
                  rpJual: 0,
                  rpStokAkhir: 0,
                  biayaOP: 0,
                  biayaPusat: 0,
                  labaRugi: 0
                });

                // Calculate percentage and per-gr values for totals
                const totalPlusMinusPersen = (totals.beratBeli + totals.stokAwalGr) > 0 ? (totals.plusMinusGr / (totals.beratBeli + totals.stokAwalGr)) * 100 : 0;
                const totalBiayaOPPerGr = totals.beratJual > 0 ? totals.biayaOP / totals.beratJual : 0;
                const totalLabaRugiPerGr = totals.beratJual > 0 ? totals.labaRugi / totals.beratJual : 0;

                return (
                  <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                    <TableCell className="font-bold text-blue-900">TOTAL</TableCell>
                    <TableCell className="text-right font-bold text-blue-900">{formatNumber(totals.stokAwalGr)}</TableCell>
                    <TableCell className="text-right font-bold text-blue-900">{formatNumber(totals.beratBeli)}</TableCell>
                    <TableCell className="text-right font-bold text-blue-900">{formatNumber(totals.beratJual)}</TableCell>
                    <TableCell className="text-right font-bold text-blue-900">{formatNumber(totals.stokAkhirGr)}</TableCell>
                    <TableCell className="text-right font-bold text-blue-900">{formatNumber(totals.plusMinusGr)}</TableCell>
                    <TableCell className={`text-right font-bold ${getPercentageColor(totalPlusMinusPersen)}`}>
                      {totalPlusMinusPersen.toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right font-bold text-blue-900">{formatCurrency(totals.rpStokAwal)}</TableCell>
                    <TableCell className="text-right font-bold text-blue-900">{formatCurrency(totals.rpBeli)}</TableCell>
                    <TableCell className="text-right font-bold text-blue-900">{formatCurrency(totals.rpJual)}</TableCell>
                    <TableCell className="text-right font-bold text-blue-900">{formatCurrency(totals.rpStokAkhir)}</TableCell>
                    <TableCell className="text-right font-bold text-blue-900">{formatCurrency(totals.biayaOP)}</TableCell>
                    <TableCell className="text-right font-bold text-blue-900">{formatNumber(totalBiayaOPPerGr)}</TableCell>
                    <TableCell className="text-right font-bold text-blue-900">{formatCurrency(totals.biayaPusat)}</TableCell>
                    <TableCell className="text-right font-bold text-blue-900">{formatCurrency(totals.labaRugi)}</TableCell>
                    <TableCell className={`text-right font-bold ${getLabaRugiColor(totalLabaRugiPerGr)}`}>
                      {formatNumber(totalLabaRugiPerGr)}
                    </TableCell>
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default Rekapan;
